import { Initial } from '@appTypes/Initial';
import { SalesPerson } from '@appTypes/SalesPerson';
import { getParsedToken } from '@services/auth/AuthService';
import { CommonService } from '@services/common/CommonService';
import { AppDispatch } from '@store/AppState';
import {
  setCurrentStoreCodeAction,
  setCurrentUserAction,
  setPermissionsAction,
} from '@store/current-user/CurrentUserActions';
import {
  setCarFaxAdvantageStatusAction,
  setCarFaxStatusAction,
  setConquestStatusAction,
  setDealerInfosAction,
  setDealerServices,
  setFeatureToggle,
  setIsLoadingDealer,
  setPitchStatus,
  setPreownedStatusAction,
  setSalesPersons,
  setSendVideoAction,
  setStoreServices,
  setTransUnionStatusAction,
  setTurnStatusAction,
} from '@store/dealer/DealerActions';
import { storeUserDealer } from '@store/userDealer/UserDealerSlice';
import { useEffect } from 'react';
import { UseQueryResult, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';

export const useGetInitial = (): UseQueryResult<Initial, any> => {
  const storeCode = localStorage.getItem('am.storeCode') as string;

  return useQuery(['initial', storeCode], () => CommonService.getInitial(storeCode), {
    staleTime: 1000 * 60 * 15,
    enabled: !!storeCode,
  });
};

const useGetSalesPersons = (): UseQueryResult<SalesPerson[], any> => {
  const storeCode = localStorage.getItem('am.storeCode') as string;

  return useQuery(['salesPersons', storeCode], () => CommonService.getSalesPersons(storeCode), {
    staleTime: 1000 * 60 * 15,
    enabled: !!storeCode,
  });
};

const uncapitalizePermissionsList = (permissionsList: string[]): string[] => {
  return permissionsList.map((permission) => {
    return permission.charAt(0).toLowerCase() + permission.slice(1);
  });
};

const saveToLocalStorage = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const useSetInitial = (): boolean => {
  const dispatch = useDispatch<AppDispatch>();
  const storeCode = localStorage.getItem('am.storeCode');

  const {
    data: salesPersonsData,
    isLoading: isSalesPersonsLoading,
    isError: isSalesPersonsError,
  } = useGetSalesPersons();

  useEffect(() => {
    if (salesPersonsData && !isSalesPersonsLoading && !isSalesPersonsError) {
      const orphanUser: SalesPerson = {
        name: 'Orphan Mastermind',
        firstName: 'Orphan',
        lastName: 'Mastermind',
        userId: 0,
        canViewDealers: [],
      };

      const conquestUser: SalesPerson = {
        name: 'Conquest Mastermind',
        firstName: 'Conquest',
        lastName: 'Mastermind',
        userId: -1,
        canViewDealers: [],
      };

      salesPersonsData.push(orphanUser);
      salesPersonsData.push(conquestUser);

      dispatch(setSalesPersons(salesPersonsData));
    }
  }, [storeCode, salesPersonsData, isSalesPersonsLoading, isSalesPersonsError, dispatch]);

  const {
    data: initialData,
    isLoading: isInitialLoading,
    isError: isInitialError,
  } = useGetInitial();

  useEffect(() => {
    if (initialData && !isInitialLoading && !isInitialError) {
      dispatch(setIsLoadingDealer(true));
      const currentUser = getParsedToken();
      currentUser.intercomUserHMAC = initialData.intercomUserHMAC;

      if (storeCode && currentUser) {
        const userDealerModel = {
          storeCode: storeCode,
          identityUserId: currentUser.userId,
          userName: currentUser.userName,
          packageType: initialData.dealerInfos[0]?.packageType,
        };

        dispatch(storeUserDealer(userDealerModel));
        saveToLocalStorage('userDealerModel', userDealerModel);
      }

      saveToLocalStorage('dealerInfos', initialData.dealerInfos);

      const brands = Array.from(new Set(initialData.dealerInfos.map((ele) => ele.brandName)));
      saveToLocalStorage('am.distinctBrands', brands);

      dispatch(setStoreServices(initialData?.storeServices));
      dispatch(
        setDealerServices(
          initialData?.dealerInfos.map((x) => {
            return {
              dealerId: x.dealerId,
              services: x.services,
            };
          }),
        ),
      );

      dispatch(setDealerInfosAction(initialData?.dealerInfos || []));
      dispatch(setCarFaxAdvantageStatusAction(initialData?.carFaxAdvantageStatus));
      dispatch(setConquestStatusAction(initialData?.storeServices.hasConquest));
      dispatch(setTurnStatusAction(initialData?.storeServices.hasTurn));
      dispatch(setTransUnionStatusAction(initialData?.storeServices.hasTransUnion));
      dispatch(setPreownedStatusAction(initialData?.storeServices.hasPreowned));
      dispatch(setFeatureToggle(initialData?.featureToggle));
      dispatch(setCarFaxStatusAction(initialData?.hasCarFax));
      dispatch(setSendVideoAction(initialData?.storeServices.hasSendVideo));
      dispatch(setPitchStatus(initialData?.pitchStatus.sort((a, b) => a.index - b.index)));

      // Set current User state after initial call
      dispatch(setCurrentUserAction(currentUser));

      // Set current User state after initial call
      dispatch(setCurrentStoreCodeAction(storeCode ?? ''));

      const formattedPerms = uncapitalizePermissionsList(initialData?.permissions);
      dispatch(setPermissionsAction(formattedPerms));

      dispatch(setIsLoadingDealer(false));
    }
  }, [storeCode, initialData, isInitialLoading, isInitialError, dispatch]);

  return isSalesPersonsLoading || isInitialLoading;
};
